import { useState, useEffect, useMemo } from 'react';
import firebase from 'firebase/compat/app';
import { TaskFormModal } from '../components/tasks';
import {
  DeleteTemplateModal,
  DeactivateTemplateModal,
  BatchActivateModal,
  BatchDeactivateModal
} from '../components/templates';
import { ConfirmModal, NotificationToast } from '../components/common';
import { getRecurrenceLabel } from '../utils/recurrenceHelpers';
import { formatDateDDMMYYYY, formatTimestampDDMMYYYY } from '../utils/dateHelpers';
import { TRANSLATIONS } from '../config/translations';

const db = firebase.firestore();
const functions = firebase.functions();

/**
 * Templates view - manage task templates
 */
export function TemplatesView({ currentUser, language }) {
  const [templates, setTemplates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [togglingTemplate, setTogglingTemplate] = useState(null);
  const [activatingTemplate, setActivatingTemplate] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(null);
  const [notification, setNotification] = useState(null);

  // Batch selection mode state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState(new Set());
  const [batchActivating, setBatchActivating] = useState(false);
  const [batchDeactivating, setBatchDeactivating] = useState(false);

  // Grouping state
  const [groupBy, setGroupBy] = useState('assignee'); // 'assignee', 'recurrence', or null

  const t = TRANSLATIONS[language];

  useEffect(() => {
    // 'active' = soft-delete flag, 'isActive' = enabled flag. Query keeps both states so admin can re-activate.
    const unsubscribeTemplates = db.collection('taskTemplates')
      .where('active', '==', true)
      .onSnapshot((snapshot) => {
        const templatesData = [];
        snapshot.forEach((doc) => {
          templatesData.push({ id: doc.id, ...doc.data() });
        });
        setTemplates(templatesData);
        setLoading(false);
      }, () => setLoading(false));

    return () => unsubscribeTemplates();
  }, []);

  useEffect(() => {
    const unsubscribeLocations = db.collection('locations')
      .where('active', '==', true)
      .onSnapshot((snapshot) => {
        const locationsData = [];
        snapshot.forEach((doc) => {
          locationsData.push({ id: doc.id, ...doc.data() });
        });
        setLocations(locationsData);
      });

    return () => unsubscribeLocations();
  }, []);

  useEffect(() => {
    const unsubscribeUsers = db.collection('users')
      .where('active', '==', true)
      .onSnapshot((snapshot) => {
        const usersData = [];
        snapshot.forEach((doc) => {
          usersData.push({ id: doc.id, ...doc.data() });
        });
        setUsers(usersData);
      });

    return () => unsubscribeUsers();
  }, []);

  // Batch selection handlers
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedTemplateIds(new Set());
  };

  const toggleTemplateSelection = (templateId) => {
    const newSelection = new Set(selectedTemplateIds);
    if (newSelection.has(templateId)) {
      newSelection.delete(templateId);
    } else {
      // Check batch limit
      if (newSelection.size >= 15) {
        setNotification({
          message: 'Maximum 15 Vorlagen können gleichzeitig ausgewählt werden',
          type: 'error'
        });
        return;
      }
      newSelection.add(templateId);
    }
    setSelectedTemplateIds(newSelection);
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedTemplateIds(new Set());
  };

  const handleBatchActivate = () => {
    const inactiveSelected = templates.filter(
      t => selectedTemplateIds.has(t.id) && !t.isActive
    );
    if (inactiveSelected.length === 0) {
      setNotification({
        message: 'Keine inaktiven Vorlagen ausgewählt',
        type: 'error'
      });
      return;
    }
    setBatchActivating(true);
  };

  const handleBatchDeactivate = () => {
    const activeSelected = templates.filter(
      t => selectedTemplateIds.has(t.id) && t.isActive
    );
    if (activeSelected.length === 0) {
      setNotification({
        message: 'Keine aktiven Vorlagen ausgewählt',
        type: 'error'
      });
      return;
    }
    setBatchDeactivating(true);
  };

  // Toggle grouping mode
  const toggleGrouping = (mode) => {
    if (groupBy === mode) {
      setGroupBy(null); // Toggle off if clicking active mode
    } else {
      setGroupBy(mode);
    }
  };

  // Group and sort templates
  const groupedTemplates = useMemo(() => {
    // Sort templates by title (ascending) for consistent ordering
    const sortedTemplates = [...templates].sort((a, b) =>
      a.title.localeCompare(b.title, 'de')
    );

    if (!groupBy) {
      // No grouping - return flat list
      return { ungrouped: sortedTemplates };
    }

    if (groupBy === 'assignee') {
      // Group by assignee
      const groups = {};
      sortedTemplates.forEach(template => {
        const assignee = users.find(u => u.id === template.assigneeId);
        const assigneeName = assignee?.displayName || 'Unbekannt';

        if (!groups[assigneeName]) {
          groups[assigneeName] = {
            name: assigneeName,
            templates: []
          };
        }
        groups[assigneeName].templates.push(template);
      });

      // Convert to array and sort by assignee name
      return Object.values(groups).sort((a, b) =>
        a.name.localeCompare(b.name, 'de')
      );
    }

    if (groupBy === 'recurrence') {
      // Group by recurrence type
      const groups = {
        daily: { name: 'Täglich', templates: [] },
        weekly: { name: 'Wöchentlich', templates: [] },
        monthly: { name: 'Monatlich', templates: [] }
      };

      sortedTemplates.forEach(template => {
        const type = template.recurrence?.type || 'daily';
        if (groups[type]) {
          groups[type].templates.push(template);
        }
      });

      // Return only non-empty groups
      return Object.values(groups).filter(g => g.templates.length > 0);
    }

    return { ungrouped: sortedTemplates };
  }, [templates, users, groupBy]);

  const handleToggleTemplate = (template, activate) => {
    if (activate) {
      setActivatingTemplate(template);
    } else {
      setTogglingTemplate(template);
    }
  };

  const confirmActivateTemplate = async () => {
    const template = activatingTemplate;
    setActivatingTemplate(null);

    try {
      const toggleTemplateStatus = functions.httpsCallable('toggleTemplateStatus');
      const result = await toggleTemplateStatus({ 
        templateId: template.id, 
        activate: true 
      });
      setNotification({
        message: `Vorlage aktiviert! Erste Instanz erstellt mit Fälligkeit: ${formatDateDDMMYYYY(result.data.dueDate)}`,
        type: 'success'
      });
    } catch (error) {
      // console statement removed for production
      setNotification({ message: 'Fehler beim Aktivieren: ' + error.message, type: 'error' });
    }
  };

  const handleDeleteTemplate = (template) => {
    setDeletingTemplate(template);
  };

  const confirmDeleteTemplate = async () => {
    try {
      const deleteTaskTemplate = functions.httpsCallable('deleteTaskTemplate');
      const result = await deleteTaskTemplate({ templateId: deletingTemplate.id });
      setDeletingTemplate(null);
      setNotification({
        message: `Vorlage gelöscht. ${result.data.deletedTasksCount} Tasks wurden entfernt.`,
        type: 'success'
      });
    } catch (error) {
      // console statement removed for production
      setDeletingTemplate(null);
      setNotification({
        message: 'Fehler beim Löschen: ' + (error.message || 'Unbekannter Fehler'),
        type: 'error'
      });
    }
  };

  if (loading) {
    return <div className="loading">{t.loading}</div>;
  }

  // Calculate selection counts
  const inactiveCount = templates.filter(t => selectedTemplateIds.has(t.id) && !t.isActive).length;
  const activeCount = templates.filter(t => selectedTemplateIds.has(t.id) && t.isActive).length;

  return (
    <div className="templates-view">
      <div className="view-header">
        <h2>Vorlagen</h2>
        <div className="header-actions">
          {!selectionMode ? (
            <>
              <button className="btn-secondary" onClick={toggleSelectionMode}>
                <i className="fas fa-check-square"></i> Auswählen
              </button>
              <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
                <i className="fas fa-plus"></i> Neue Vorlage
              </button>
            </>
          ) : (
            <button className="btn-secondary" onClick={exitSelectionMode}>
              <i className="fas fa-times"></i> Abbrechen
            </button>
          )}
        </div>
      </div>

      {/* Grouping Segmented Control */}
      <div className="grouping-controls">
        <label>Gruppieren nach:</label>
        <div className="segmented-control">
          <button
            className={groupBy === 'assignee' ? 'active' : ''}
            onClick={() => toggleGrouping('assignee')}
          >
            <i className="fas fa-user"></i> Zuständiger
          </button>
          <button
            className={groupBy === 'recurrence' ? 'active' : ''}
            onClick={() => toggleGrouping('recurrence')}
          >
            <i className="fas fa-redo"></i> Wiederholung
          </button>
        </div>
      </div>

      {templates.length === 0 ? (
        <p className="no-data">Keine Vorlagen vorhanden</p>
      ) : groupBy ? (
        // Grouped view
        <div className="task-groups">
          {groupedTemplates.map(group => (
            <div key={group.name} className="task-group">
              <h3>
                {groupBy === 'assignee' ? (
                  <><i className="fas fa-user"></i> {group.name}</>
                ) : (
                  <><i className="fas fa-redo"></i> {group.name}</>
                )}
                {' '}({group.templates.length})
              </h3>
              <div className="task-cards">
                {group.templates.map(template => {
                  const location = locations.find(l => l.id === template.locationId);
                  const assignee = users.find(u => u.id === template.assigneeId);
                  const isActive = template.isActive === true;
                  const isSelected = selectedTemplateIds.has(template.id);

                  return (
                    <div
                      key={template.id}
                      className={`template-card ${isActive ? 'template-active' : 'template-inactive'} ${isSelected ? 'template-selected' : ''}`}
                      onClick={() => {
                        if (selectionMode) {
                          toggleTemplateSelection(template.id);
                        } else {
                          setEditingTemplate(template);
                        }
                      }}
                    >
                      {selectionMode && (
                        <div className="template-checkbox">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleTemplateSelection(template.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      )}
                      <div className="template-card-header">
                        <h3>
                          {template.title}
                          {template.stepsTotalCount > 0 && (
                            <span className="badge badge-info" style={{ marginLeft: '8px', fontSize: '12px' }}>
                              {template.stepsTotalCount} {template.stepsTotalCount === 1 ? 'Schritt' : 'Schritte'}
                            </span>
                          )}
                        </h3>
                        <span className={`badge ${isActive ? 'badge-success' : 'badge-inactive'}`}>
                          {isActive ? 'Aktiviert' : 'Inaktiv'}
                        </span>
                      </div>

                      <p className="template-description">{template.description}</p>

                      <div className="template-meta">
                        <div className="meta-item">
                          <i className="fas fa-map-marker-alt"></i>
                          <span>{location?.name || 'Unbekannt'}</span>
                        </div>

                        <div className="meta-item">
                          <i className="fas fa-user"></i>
                          <span>{assignee?.displayName || 'Unbekannt'}</span>
                        </div>

                        <div className="meta-item">
                          <i className="fas fa-redo"></i>
                          <span>{getRecurrenceLabel(template.recurrence)}</span>
                        </div>

                        {template.photoRequired && (
                          <div className="meta-item">
                            <i className="fas fa-camera"></i>
                            <span>Foto erforderlich</span>
                          </div>
                        )}

                        {isActive && template.lastActivated && (
                          <div className="meta-item">
                            <i className="fas fa-clock"></i>
                            <span>
                              Aktiviert: {formatTimestampDDMMYYYY(template.lastActivated)}
                            </span>
                          </div>
                        )}

                        {!isActive && template.lastDeactivated && (
                          <div className="meta-item">
                            <i className="fas fa-pause-circle"></i>
                            <span>
                              Deaktiviert: {formatTimestampDDMMYYYY(template.lastDeactivated)}
                            </span>
                          </div>
                        )}
                      </div>

                      {!selectionMode && (
                        <div className="template-actions">
                          {isActive ? (
                            <button
                              className="btn-warning"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleTemplate(template, false);
                              }}
                            >
                              <i className="fas fa-pause"></i> Deaktivieren
                            </button>
                          ) : (
                            <>
                              <button
                                className="btn-success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleTemplate(template, true);
                                }}
                              >
                                <i className="fas fa-play"></i> Aktivieren
                              </button>
                              <button
                                className="btn-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTemplate(template);
                                }}
                              >
                                <i className="fas fa-trash"></i> Löschen
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Ungrouped flat view
        <div className="task-cards">
          {groupedTemplates.ungrouped.map(template => {
            const location = locations.find(l => l.id === template.locationId);
            const assignee = users.find(u => u.id === template.assigneeId);
            const isActive = template.isActive === true;

            const isSelected = selectedTemplateIds.has(template.id);

            return (
              <div
                key={template.id}
                className={`template-card ${isActive ? 'template-active' : 'template-inactive'} ${isSelected ? 'template-selected' : ''}`}
                onClick={() => {
                  if (selectionMode) {
                    toggleTemplateSelection(template.id);
                  } else {
                    setEditingTemplate(template);
                  }
                }}
              >
                {selectionMode && (
                  <div className="template-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleTemplateSelection(template.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}
                <div className="template-card-header">
                  <h3>
                    {template.title}
                    {template.stepsTotalCount > 0 && (
                      <span className="badge badge-info" style={{ marginLeft: '8px', fontSize: '12px' }}>
                        {template.stepsTotalCount} {template.stepsTotalCount === 1 ? 'Schritt' : 'Schritte'}
                      </span>
                    )}
                  </h3>
                  <span className={`badge ${isActive ? 'badge-success' : 'badge-inactive'}`}>
                    {isActive ? 'Aktiviert' : 'Inaktiv'}
                  </span>
                </div>

                <p className="template-description">{template.description}</p>

                <div className="template-meta">
                  <div className="meta-item">
                    <i className="fas fa-map-marker-alt"></i>
                    <span>{location?.name || 'Unbekannt'}</span>
                  </div>

                  <div className="meta-item">
                    <i className="fas fa-user"></i>
                    <span>{assignee?.displayName || 'Unbekannt'}</span>
                  </div>

                  <div className="meta-item">
                    <i className="fas fa-redo"></i>
                    <span>{getRecurrenceLabel(template.recurrence)}</span>
                  </div>

                  {template.photoRequired && (
                    <div className="meta-item">
                      <i className="fas fa-camera"></i>
                      <span>Foto erforderlich</span>
                    </div>
                  )}

                  {isActive && template.lastActivated && (
                    <div className="meta-item">
                      <i className="fas fa-clock"></i>
                      <span>
                        Aktiviert: {formatTimestampDDMMYYYY(template.lastActivated)}
                      </span>
                    </div>
                  )}

                  {!isActive && template.lastDeactivated && (
                    <div className="meta-item">
                      <i className="fas fa-pause-circle"></i>
                      <span>
                        Deaktiviert: {formatTimestampDDMMYYYY(template.lastDeactivated)}
                      </span>
                    </div>
                  )}
                </div>

                {!selectionMode && (
                  <div className="template-actions">
                    {isActive ? (
                      <button
                        className="btn-warning"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleTemplate(template, false);
                        }}
                      >
                        <i className="fas fa-pause"></i> Deaktivieren
                      </button>
                    ) : (
                      <>
                        <button
                          className="btn-success"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleTemplate(template, true);
                          }}
                        >
                          <i className="fas fa-play"></i> Aktivieren
                        </button>
                        <button
                          className="btn-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTemplate(template);
                          }}
                        >
                          <i className="fas fa-trash"></i> Löschen
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Floating Action Toolbar */}
      {selectionMode && selectedTemplateIds.size > 0 && (
        <div className="batch-selection-toolbar">
          <div className="toolbar-content">
            <div className="selection-info">
              <i className="fas fa-check-square"></i>
              <span>{selectedTemplateIds.size} Vorlagen ausgewählt</span>
            </div>
            <div className="toolbar-actions">
              <button
                className="btn-success"
                onClick={handleBatchActivate}
                disabled={inactiveCount === 0}
              >
                <i className="fas fa-play"></i> Aktivieren ({inactiveCount})
              </button>
              <button
                className="btn-warning"
                onClick={handleBatchDeactivate}
                disabled={activeCount === 0}
              >
                <i className="fas fa-pause"></i> Deaktivieren ({activeCount})
              </button>
              <button className="btn-secondary" onClick={exitSelectionMode}>
                <i className="fas fa-times"></i> Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <TaskFormModal
          locations={locations}
          users={users}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            setNotification({ message: 'Vorlage erstellt', type: 'success' });
          }}
        />
      )}

      {editingTemplate && (
        <TaskFormModal
          task={editingTemplate}
          locations={locations}
          users={users}
          onClose={() => setEditingTemplate(null)}
          onSuccess={() => {
            setEditingTemplate(null);
            setNotification({ message: 'Vorlage aktualisiert', type: 'success' });
          }}
        />
      )}

      {togglingTemplate && (
        <DeactivateTemplateModal
          template={togglingTemplate}
          onClose={() => setTogglingTemplate(null)}
          onConfirm={async () => {
            const template = togglingTemplate;
            setTogglingTemplate(null);
            
            try {
              const toggleTemplateStatus = functions.httpsCallable('toggleTemplateStatus');
              const result = await toggleTemplateStatus({ 
                templateId: template.id, 
                activate: false 
              });
              setNotification({ 
                message: `Vorlage deaktiviert. ${result.data.deletedTasksCount} offene Tasks wurden gelöscht.`, 
                type: 'success' 
              });
            } catch (error) {
              // console statement removed for production
              setNotification({ message: 'Fehler beim Deaktivieren: ' + error.message, type: 'error' });
            }
          }}
        />
      )}

      {activatingTemplate && (
        <ConfirmModal
          title="Vorlage aktivieren"
          message="Möchten Sie diese Vorlage aktivieren? Es wird die erste Aufgaben-Instanz erstellt."
          confirmText="Aktivieren"
          confirmClass="btn-success"
          onConfirm={confirmActivateTemplate}
          onClose={() => setActivatingTemplate(null)}
        />
      )}

      {deletingTemplate && (
        <DeleteTemplateModal
          template={deletingTemplate}
          onClose={() => setDeletingTemplate(null)}
          onSuccess={confirmDeleteTemplate}
        />
      )}

      {batchActivating && (
        <BatchActivateModal
          templates={templates.filter(t => selectedTemplateIds.has(t.id) && !t.isActive)}
          onClose={() => setBatchActivating(false)}
          onSuccess={(result) => {
            setBatchActivating(false);
            exitSelectionMode();

            if (result.failureCount === 0) {
              setNotification({
                message: `${result.successCount} Vorlagen erfolgreich aktiviert`,
                type: 'success'
              });
            } else if (result.successCount === 0) {
              setNotification({
                message: `Fehler beim Aktivieren: ${result.failedTemplates.join(', ')}`,
                type: 'error'
              });
            } else {
              setNotification({
                message: `${result.successCount} Vorlagen aktiviert, ${result.failureCount} Fehler: ${result.failedTemplates.join(', ')}`,
                type: 'warning'
              });
            }
          }}
        />
      )}

      {batchDeactivating && (
        <BatchDeactivateModal
          templates={templates.filter(t => selectedTemplateIds.has(t.id) && t.isActive)}
          onClose={() => setBatchDeactivating(false)}
          onSuccess={(result) => {
            setBatchDeactivating(false);
            exitSelectionMode();

            if (result.failureCount === 0) {
              setNotification({
                message: `${result.successCount} Vorlagen deaktiviert. ${result.totalDeletedTasks} offene Tasks wurden gelöscht.`,
                type: 'success'
              });
            } else if (result.successCount === 0) {
              setNotification({
                message: `Fehler beim Deaktivieren: ${result.failedTemplates.join(', ')}`,
                type: 'error'
              });
            } else {
              setNotification({
                message: `${result.successCount} Vorlagen deaktiviert, ${result.failureCount} Fehler: ${result.failedTemplates.join(', ')}`,
                type: 'warning'
              });
            }
          }}
        />
      )}

      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
}


// ============================================================================
// DEACTIVATE TEMPLATE MODAL - Shows warning with task count
// ============================================================================

